import { useState } from "react";
import { Badge, ActionMenu } from "./SharedUI";
import { fmt } from "./Utils/Utilities";

/* ─── mock detail data ─── */
const DETAIL = {
  id: "ACC-0042",
  customer: "Meridian Industrial Group",
  contactName: "Sandra Okafor",
  contactEmail: "s.okafor@meridian.co",
  contactPhone: "+27 11 555 0192",
  industry: "Mining & Resources",
  region: "Gauteng",
  creditLimit: 480000,
  usedCredit: 312400,
  rating: "B",
  status: "Active",
  paymentTerms: "Net 30",
  openInvoices: 7,
  overdueAmount: 48200,
  joinDate: "2021-03-14",
  lastActivity: "2025-04-28",
  accountManager: "Thabo Nkosi",
  notes: "High-volume client. Seasonal demand peaks in Q2. Prefers EFT payments.",
  invoices: [
    { id: "INV-8821", date: "2025-04-22", due: "2025-05-22", amount: 84000, status: "Open" },
    { id: "INV-8744", date: "2025-04-01", due: "2025-05-01", amount: 63200, status: "Overdue" },
    { id: "INV-8691", date: "2025-03-18", due: "2025-04-18", amount: 112000, status: "Overdue" },
    { id: "INV-8600", date: "2025-02-28", due: "2025-03-28", amount: 53200, status: "Paid" },
    { id: "INV-8521", date: "2025-01-15", due: "2025-02-15", amount: 97800, status: "Paid" },
  ],
  activity: [
    { icon: "bi-file-earmark-text", color: "var(--blue)", text: "Invoice INV-8821 issued", time: "2 days ago" },
    { icon: "bi-cash-stack", color: "var(--success)", text: "Payment of R53,200 received", time: "5 days ago" },
    { icon: "bi-exclamation-triangle", color: "var(--warn)", text: "INV-8744 overdue — auto-reminder sent", time: "8 days ago" },
    { icon: "bi-telephone", color: "var(--teal)", text: "Call logged by Thabo Nkosi", time: "12 days ago" },
    { icon: "bi-lock", color: "var(--danger)", text: "Credit limit review triggered", time: "18 days ago" },
  ],
};

const ratingColor = { A: "var(--success)", B: "var(--teal)", C: "var(--amber)", D: "var(--warn)", F: "var(--danger)" };

const invoiceStatusStyle = {
  Open: { color: "var(--blue)", bg: "rgba(64,196,255,.1)", border: "rgba(64,196,255,.25)" },
  Overdue: { color: "var(--danger)", bg: "rgba(255,23,68,.1)", border: "rgba(255,23,68,.25)" },
  Paid: { color: "var(--success)", bg: "rgba(0,230,118,.1)", border: "rgba(0,230,118,.25)" },
};

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: accent }} />
      <div className="fs11 tc3 mb8">{label}</div>
      <div className="syne fw7 fs22">{value}</div>
      {sub && <div className="fs10 tc3" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function AccountDetailPage({ showToast: toast, account: rawAccount, onBack, onEdit }) {
  const account = { ...DETAIL, ...rawAccount, invoices: rawAccount?.invoices ?? DETAIL.invoices, activity: rawAccount?.activity ?? DETAIL.activity };
  const [tab, setTab] = useState("invoices");
  const util = Math.round((account.usedCredit / account.creditLimit) * 100);

  return (
    <div className="page">
      {/* ── breadcrumb / back ── */}
      <div className="fc gap8 mb16">
        <button
          className="btn btn-g btn-sm"
          onClick={onBack}
          style={{ gap: 5 }}
        >
          <i className="bi bi-arrow-left" style={{ fontSize: 11 }} />
          Accounts
        </button>
        <span className="tc3 fs11">/</span>
        <span className="fs11 tc2">{account.id}</span>
      </div>

      {/* ── hero row ── */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 22px", marginBottom: 16, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        {/* avatar */}
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,var(--flame),var(--amber))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 0 24px rgba(255,79,23,.3)", flexShrink: 0 }}>
          🏭
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className="syne fw7" style={{ fontSize: 18 }}>{account.customer}</span>
            <span className="mono t-flame" style={{ fontSize: 11 }}>{account.id}</span>
            <Badge status={account.status} />
            <span className="syne fw7 fs14" style={{ color: ratingColor[account.rating] }}>
              {account.rating}
            </span>
          </div>
          <div className="fc gap12 mt4" style={{ marginTop: 6, flexWrap: "wrap" }}>
            <span className="fs11 tc2"><i className="bi bi-person" style={{ marginRight: 4 }} />{account.contactName}</span>
            <span className="fs11 tc2"><i className="bi bi-envelope" style={{ marginRight: 4 }} />{account.contactEmail}</span>
            <span className="fs11 tc2"><i className="bi bi-telephone" style={{ marginRight: 4 }} />{account.contactPhone}</span>
            <span className="fs11 tc2"><i className="bi bi-geo-alt" style={{ marginRight: 4 }} />{account.region}</span>
          </div>
        </div>
        <div className="fc gap8">
          <button className="btn btn-p btn-sm" onClick={() => toast("Invoice created", "s")}>
            <i className="bi bi-plus" /> New Invoice
          </button>
          <ActionMenu
            items={[
              { icon: <i className="bi bi-pencil" />, label: "Edit Account", action: () => { if (typeof onEdit === "function") onEdit(account); else toast("Edit account", "s"); } },
              { icon: <i className="bi bi-lock" />, label: "Block Account", action: () => toast("Account blocked", "w"), danger: true },
              { icon: <i className="bi bi-wallet2" />, label: "Adjust Limit", action: () => toast("Credit adjustment", "s") },
              { icon: <i className="bi bi-download" />, label: "Export PDF", action: () => toast("Exported", "s") },
            ]}
          />
        </div>
      </div>

      {/* ── stat cards ── */}
      <div className="g3 mb16" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <StatCard
          label="Credit Limit"
          value={fmt.currency(account.creditLimit)}
          sub={`Terms: ${account.paymentTerms}`}
          accent="linear-gradient(90deg,var(--blue),var(--teal))"
        />
        <StatCard
          label="Outstanding Balance"
          value={<span className="t-danger">{fmt.currency(account.usedCredit)}</span>}
          sub={`${util}% utilization`}
          accent="linear-gradient(90deg,var(--flame),var(--amber))"
        />
        <StatCard
          label="Overdue Amount"
          value={<span className="t-amber">{fmt.currency(account.overdueAmount)}</span>}
          sub={`${account.openInvoices} open invoices`}
          accent="linear-gradient(90deg,var(--amber),var(--flame))"
        />
        <StatCard
          label="Available Credit"
          value={<span className="t-success">{fmt.currency(account.creditLimit - account.usedCredit)}</span>}
          sub={`Since ${new Date(account.joinDate).toLocaleDateString("en-ZA", { year: "numeric", month: "short" })}`}
          accent="linear-gradient(90deg,var(--success),var(--teal))"
        />
      </div>

      {/* ── utilization bar ── */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px", marginBottom: 16 }}>
        <div className="fb mb8">
          <span className="fs11 fw6">Credit Utilization</span>
          <span className="mono fs11 tc2">{fmt.currency(account.usedCredit)} / {fmt.currency(account.creditLimit)}</span>
        </div>
        <div className="prog-wrap" style={{ height: 8 }}>
          <div className="prog-fill" style={{
            width: `${util}%`,
            background: util > 80 ? "var(--danger)" : util > 60 ? "var(--amber)" : "var(--teal)",
          }} />
        </div>
      </div>

      {/* ── main content grid ── */}
      <div className="g6040">
        {/* LEFT: invoices / activity tabs */}
        <div className="panel">
          <div className="ph">
            <span className="ph-title">Transactions</span>
            <div className="tabs" style={{ marginLeft: "auto" }}>
              {["invoices", "activity"].map((t) => (
                <div key={t} className={`tab${tab === t ? " on" : ""}`} onClick={() => setTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </div>
              ))}
            </div>
          </div>

          {tab === "invoices" && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Issued</th>
                  <th>Due</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {account.invoices.map((inv) => {
                  const s = invoiceStatusStyle[inv.status];
                  return (
                    <tr key={inv.id}>
                      <td className="mono t-flame">{inv.id}</td>
                      <td className="fs11 tc2">{new Date(inv.date).toLocaleDateString("en-ZA")}</td>
                      <td className="fs11 tc2">{new Date(inv.due).toLocaleDateString("en-ZA")}</td>
                      <td className="mono fw7">{fmt.currency(inv.amount)}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.color, display: "inline-block" }} />
                          {inv.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-g btn-sm btn-ico" onClick={() => toast(`${inv.id} opened`, "s")}>
                          <i className="bi bi-arrow-up-right" style={{ fontSize: 10 }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {tab === "activity" && (
            <div style={{ padding: 18 }}>
              {account.activity.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 12, paddingBottom: 16, position: "relative" }}>
                  {i < account.activity.length - 1 && (
                    <div style={{ position: "absolute", left: 13, top: 28, bottom: 0, width: 1, background: "var(--border)" }} />
                  )}
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--elevated)", border: `1px solid var(--border)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: a.color }}>
                    <i className={`bi ${a.icon}`} />
                  </div>
                  <div>
                    <div className="fs12 fw6" style={{ marginBottom: 2 }}>{a.text}</div>
                    <div className="fs10 tc3">{a.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: account info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="panel">
            <div className="ph"><span className="ph-title">Account Info</span></div>
            <div style={{ padding: "14px 18px" }}>
              {[
                ["Account Manager", account.accountManager],
                ["Industry", account.industry],
                ["Region", account.region],
                ["Payment Terms", account.paymentTerms],
                ["Member Since", new Date(account.joinDate).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })],
                ["Last Activity", new Date(account.lastActivity).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })],
              ].map(([k, v]) => (
                <div key={k} className="fb" style={{ paddingBottom: 10, marginBottom: 10, borderBottom: "1px solid var(--border)" }}>
                  <span className="fs11 tc3">{k}</span>
                  <span className="fs11 fw6">{v}</span>
                </div>
              ))}
              <div>
                <span className="fs11 tc3" style={{ display: "block", marginBottom: 6 }}>Notes</span>
                <p className="fs11 tc2" style={{ lineHeight: 1.6 }}>{account.notes}</p>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="ph"><span className="ph-title">Quick Actions</span></div>
            <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                { icon: "bi-envelope", label: "Send Statement", type: "btn-s" },
                { icon: "bi-telephone", label: "Log Call", type: "btn-s" },
                { icon: "bi-shield-check", label: "Run Credit Check", type: "btn-s" },
                { icon: "bi-file-earmark-arrow-down", label: "Export Account", type: "btn-g" },
              ].map(({ icon, label, type }) => (
                <button key={label} className={`btn ${type}`} style={{ justifyContent: "flex-start", width: "100%" }} onClick={() => toast(label, "s")}>
                  <i className={`bi ${icon}`} style={{ fontSize: 13 }} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountDetailPage;